import dayjs from 'dayjs';
import { businesses } from '~api';
import { Handler } from '~services';
import i18n from '~/i18n';
import { ALL_BUSINESSES, PRESETS } from '~/models/ActivityReport';

const SET_FILTER = 'SET_FILTER';
const SET_REPORT = 'SET_REPORT';
const SET_LOADING = 'SET_LOADING';
const SET_ERROR = 'SET_ERROR';
const SET_NEVER_LOGGED_IN_LOADING = 'SET_NEVER_LOGGED_IN_LOADING';

const defaultFilter = () => ({
    businessId: ALL_BUSINESSES,
    ...PRESETS.last30(dayjs())
});

const state = {
    filter: defaultFilter(),
    report: null,
    isLoading: false,
    error: null,
    isNeverLoggedInLoading: false
};

const actions = {
    setFilter({ commit }, patch) {
        commit(SET_FILTER, patch);
    },

    applyPreset({ commit }, key) {
        const preset = PRESETS[key];
        if (!preset) return;
        commit(SET_FILTER, preset(dayjs()));
    },

    /**
     * Load the report for the current filter.
     * @returns {Promise<object|null>} the report, or null on failure
     */
    async fetchReport({ commit, state, getters }) {
        if (!getters.isRangeValid) {
            commit(SET_ERROR, 'invalidRange');
            return null;
        }

        const params = {
            dateFrom: state.filter.dateFrom,
            dateTo: state.filter.dateTo
        };
        if (state.filter.businessId) {
            params.businessId = state.filter.businessId;
        }

        commit(SET_LOADING, true);
        commit(SET_ERROR, null);

        try {
            const resp = await businesses.activityReport(params);

            if (resp.ok) {
                commit(SET_REPORT, resp.data);
                return resp.data;
            }

            commit(SET_REPORT, null);
            commit(SET_ERROR, 'failed');
            Handler.handleResponse(resp);
            return null;
        } finally {
            commit(SET_LOADING, false);
        }
    },

    /**
     * Load every member who has never logged in (all time) for the current
     * business filter. Independent of the date range and of the main report
     * state.
     * @returns {Promise<object|null>} the payload, or null on failure
     */
    async fetchNeverLoggedIn({ commit, state }) {
        const params = {};
        if (state.filter.businessId) {
            params.businessId = state.filter.businessId;
        }

        commit(SET_NEVER_LOGGED_IN_LOADING, true);

        try {
            const resp = await businesses.neverLoggedInUsers(params);

            if (resp.ok) return resp.data;

            Handler.handleResponse(
                resp,
                undefined,
                i18n.t('activityReport.neverLoggedIn.failed')
            );
            return null;
        } finally {
            commit(SET_NEVER_LOGGED_IN_LOADING, false);
        }
    }
};

const mutations = {
    [SET_FILTER](state, patch) {
        state.filter = { ...state.filter, ...patch };
    },
    [SET_REPORT](state, report) {
        state.report = report;
    },
    [SET_LOADING](state, value) {
        state.isLoading = value;
    },
    [SET_ERROR](state, value) {
        state.error = value;
    },
    [SET_NEVER_LOGGED_IN_LOADING](state, value) {
        state.isNeverLoggedInLoading = value;
    }
};

const getters = {
    filter: state => state.filter,
    report: state => state.report,
    isLoading: state => state.isLoading,
    error: state => state.error,
    isNeverLoggedInLoading: state => state.isNeverLoggedInLoading,
    hasReport: state => !!state.report,
    isRangeValid: state => {
        const { dateFrom, dateTo } = state.filter;
        if (!dateFrom || !dateTo) return false;
        return !dayjs(dateFrom).isAfter(dayjs(dateTo));
    },
    daily: state => (state.report ? state.report.daily : []),
    /**
     * Derived headline numbers. `meditatedRate` is the share of users who
     * logged in and also meditated (0..1), or null when nobody logged in.
     */
    totals: state => {
        if (!state.report) return null;
        const { members, uniqueLogins, uniqueMeditated } = state.report.totals;
        return {
            members,
            uniqueLogins,
            uniqueMeditated,
            meditatedRate: uniqueLogins ? uniqueMeditated / uniqueLogins : null
        };
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
};
